import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { LinkSimple, Plus, Pencil, Trash, Play } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WebhooksPage = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    events: [],
    is_active: true,
    headers: ''
  });

  const eventOptions = [
    'status_change',
    'record_created',
    'record_updated'
  ];

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/webhooks`);
      setWebhooks(data);
    } catch (e) {
      toast.error('Failed to fetch webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let headers = {};
    if (formData.headers) {
      try {
        headers = JSON.parse(formData.headers);
      } catch {
        toast.error('Invalid headers JSON');
        return;
      }
    }

    try {
      const payload = {
        name: formData.name,
        url: formData.url,
        events: formData.events,
        is_active: formData.is_active,
        headers
      };

      if (editingWebhook) {
        await axios.put(`${API_URL}/api/webhooks/${editingWebhook.id}`, payload);
        toast.success('Webhook updated');
      } else {
        await axios.post(`${API_URL}/api/webhooks`, payload);
        toast.success('Webhook created');
      }
      fetchWebhooks();
      closeDialog();
    } catch (e) {
      toast.error('Failed to save webhook');
    }
  };

  const handleDelete = async (webhookId) => {
    if (!window.confirm('Delete this webhook?')) return;
    try {
      await axios.delete(`${API_URL}/api/webhooks/${webhookId}`);
      toast.success('Webhook deleted');
      fetchWebhooks();
    } catch (e) {
      toast.error('Failed to delete webhook');
    }
  };

  const toggleEvent = (event) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };

  const openEditDialog = (webhook) => {
    setEditingWebhook(webhook);
    setFormData({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events,
      is_active: webhook.is_active,
      headers: JSON.stringify(webhook.headers || {}, null, 2)
    });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingWebhook(null);
    setFormData({ name: '', url: '', events: [], is_active: true, headers: '' });
  };

  if (loading) {
    return <div className="h-96 skeleton rounded-md"></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Webhooks</h1>
          <p className="text-muted-foreground">Configure webhook notifications</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => closeDialog()} data-testid="add-webhook-btn">
              <Plus size={18} className="mr-2" />
              Add Webhook
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingWebhook ? 'Edit Webhook' : 'Add Webhook'}</DialogTitle>
              <DialogDescription>Configure webhook endpoint</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Status Notifications"
                  required
                  data-testid="webhook-name"
                />
              </div>
              <div className="space-y-2">
                <Label>URL</Label>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  placeholder="https://example.com/webhook"
                  required
                  data-testid="webhook-url"
                />
              </div>
              <div className="space-y-2">
                <Label>Events</Label>
                <div className="flex flex-wrap gap-2">
                  {eventOptions.map(event => (
                    <Button
                      key={event}
                      type="button"
                      variant={formData.events.includes(event) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => toggleEvent(event)}
                      data-testid={`toggle-event-${event}`}
                    >
                      {event.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Headers (JSON, optional)</Label>
                <Input
                  value={formData.headers}
                  onChange={(e) => setFormData({...formData, headers: e.target.value})}
                  placeholder='{"Authorization": "Bearer xxx"}'
                  data-testid="webhook-headers"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  data-testid="webhook-active"
                />
                <Label>Active</Label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={closeDialog}>Cancel</Button>
                <Button type="submit" data-testid="save-webhook-btn">
                  {editingWebhook ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Events</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {webhooks.length > 0 ? (
                webhooks.map(webhook => (
                  <TableRow key={webhook.id}>
                    <TableCell className="font-medium">{webhook.name}</TableCell>
                    <TableCell className="max-w-xs truncate">{webhook.url}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {webhook.events?.map(event => (
                          <Badge key={event} variant="outline" className="text-xs">
                            {event.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={webhook.is_active ? 'default' : 'secondary'}>
                        {webhook.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(webhook)} data-testid={`edit-webhook-${webhook.id}`}>
                          <Pencil size={16} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(webhook.id)} className="text-destructive" data-testid={`delete-webhook-${webhook.id}`}>
                          <Trash size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12">
                    <LinkSimple size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-muted-foreground">No webhooks configured</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default WebhooksPage;
